// ProjectPhases.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Calendar, DollarSign, Package, FileText } from 'lucide-react';
import supabase from '../../supabase';
import './ProjectPhases.css';

const ProjectPhases = ({ project, onProjectUpdate, mode = 'all', selectedPhaseId = null }) => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phase_order: 1,
    status: 'Not Active',
    start_date: '',
    end_date: '',
    budget_allocation: '',
    deliverables: []
  });
  const [deliverableInput, setDeliverableInput] = useState({ name: '', description: '' });

  useEffect(() => {
    if (project?.id) {
      fetchPhases();
    }
  }, [project?.id]);

  const fetchPhases = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', project.id)
        .order('phase_order', { ascending: true });
      if (error) throw error;
      setPhases(data || []);
    } catch (err) {
      console.error('Error fetching phases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeliverableChange = (e) => {
    const { name, value } = e.target;
    setDeliverableInput(prev => ({ ...prev, [name]: value }));
  };

  const addDeliverable = () => {
    if (deliverableInput.name.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, { ...deliverableInput, id: Date.now() }]
      }));
      setDeliverableInput({ name: '', description: '' });
    }
  };

  const removeDeliverable = (id) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter(d => d.id !== id)
    }));
  };

  const startCreating = () => {
    const nextOrder = phases.length > 0 ? Math.max(...phases.map(p => p.phase_order)) + 1 : 1;
    setFormData({
      name: '',
      description: '',
      phase_order: nextOrder,
      status: 'Not Active',
      start_date: '',
      end_date: '',
      budget_allocation: '',
      deliverables: []
    });
    setIsCreating(true);
    setEditingPhaseId(null);
    setIsModalOpen(true);
  };

  const startEditing = (phase) => {
    setFormData({
      name: phase.name,
      description: phase.description || '',
      phase_order: phase.phase_order,
      status: phase.status,
      start_date: phase.start_date || '',
      end_date: phase.end_date || '',
      budget_allocation: phase.budget_allocation || '',
      deliverables: phase.deliverables || []
    });
    setEditingPhaseId(phase.id);
    setIsCreating(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    cancelForm();
  };

  const cancelForm = () => {
    setIsCreating(false);
    setEditingPhaseId(null);
    setFormData({
      name: '',
      description: '',
      phase_order: 1,
      status: 'Not Active',
      start_date: '',
      end_date: '',
      budget_allocation: '',
      deliverables: []
    });
    setDeliverableInput({ name: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Phase name is required');
      return;
    }
    try {
      const phaseData = {
        project_id: project.id,
        name: formData.name,
        description: formData.description || null,
        phase_order: parseInt(formData.phase_order),
        status: formData.status,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        budget_allocation: formData.budget_allocation ? parseFloat(formData.budget_allocation) : null,
        deliverables: formData.deliverables.length > 0 ? formData.deliverables : null,
        updated_at: new Date().toISOString()
      };

      if (editingPhaseId) {
        const { error } = await supabase
          .from('project_phases')
          .update(phaseData)
          .eq('id', editingPhaseId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('project_phases')
          .insert([phaseData]);
        if (error) throw error;
      }
      await fetchPhases();
      closeModal();
      if (onProjectUpdate) onProjectUpdate();
    } catch (err) {
      console.error('Error saving phase:', err);
      alert('Failed to save phase');
    }
  };

  const handleDelete = async (phaseId) => {
    if (!window.confirm('Are you sure you want to delete this phase?')) return;
    try {
      const { error } = await supabase
        .from('project_phases')
        .delete()
        .eq('id', phaseId);
      if (error) throw error;
      await fetchPhases();
      if (onProjectUpdate) onProjectUpdate();
    } catch (err) {
      console.error('Error deleting phase:', err);
      alert('Failed to delete phase');
    }
  };

  const toggleStatus = async (phase) => {
    const statusOrder = ['Not Active', 'Active', 'Completed'];
    const currentIndex = statusOrder.indexOf(phase.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    try {
      const { error } = await supabase
        .from('project_phases')
        .update({ status: nextStatus, updated_at: new Date().toISOString() })
        .eq('id', phase.id);
      if (error) throw error;
      await fetchPhases();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleGenerateInvoice = async (phase) => {
    if (phase.status !== 'Completed') {
      alert('Phase must be in Completed status to generate invoice');
      return;
    }
    if (phase.invoice_created) {
      alert('Invoice already generated for this phase');
      return;
    }
    if (!window.confirm(`Generate invoice for phase "${phase.name}"?`)) {
      return;
    }
    try {
      setGeneratingInvoice(true);
      
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      console.log('Invoking Edge Function with:', { phaseId: phase.id, userId: user.id });

      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke('generate-phase-invoice', {
        body: { phaseId: phase.id, userId: user.id }
      });

      console.log('Edge Function response:', { data, error });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(error.message || 'Failed to generate invoice');
      }

      if (!data || !data.success) {
        throw new Error(data?.error || 'Unknown error occurred');
      }
      
      // The Edge Function already updates the phase, but let's fetch to be sure
      await fetchPhases();
      if (onProjectUpdate) onProjectUpdate();

      alert(`✓ ${data.message}\n\nInvoice Number: ${data.invoice.invoice_number}\nAmount: ₹${data.invoice.total_amount.toFixed(2)}`);
      
    } catch (err) {
      console.error('Error generating invoice:', err);
      alert(`Failed to generate invoice: ${err.message}`);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = { 
      'Not Active': '#666', 
      'Active': '#2254c5', 
      'Completed': '#000'
    };
    return colors[status] || '#666';
  };

  if (loading) {
    return (
      <div className="prjctphase-loading">
        <div className="prjctphase-spinner" />
        <p>Loading phases...</p>
      </div>
    );
  }

  const visiblePhases = phases.filter(p => mode === 'all' || p.id === selectedPhaseId);

  return (
    <div className="prjctphase-container">
      {/* Header */}
      <div className="prjctphase-header">
        <h2>Project Phases</h2>
        <button className="prjctphase-btn-primary" onClick={startCreating}>
          <Plus size={18} /> Add Phase
        </button>
      </div>

      {/* Empty state */}
      {visiblePhases.length === 0 ? (
        <div className="prjctphase-empty">
          <p>No phases created yet</p>
          <button className="prjctphase-btn-primary" onClick={startCreating}>
            <Plus size={18} /> Create your first phase
          </button>
        </div>
      ) : (
        // Single continuous flow (no cards/boxes)
        <div className="prjctphase-flow">
          {visiblePhases.map((phase) => (
            <div key={phase.id} className="prjctphase-flow-item">
              <div className="prjctphase-flow-head">
                <span className="prjctphase-order">{phase.phase_order}</span>
                <h3 className="prjctphase-flow-title">{phase.name}</h3>
                <div className="prjctphase-flow-actions">
                  {phase.invoice_created ? (
                    // Show only status badge (non-clickable) when invoice is created
                    <span
                      className="prjctphase-status-badge"
                      style={{ backgroundColor: getStatusColor(phase.status), cursor: 'default' }}
                      title="Invoice generated - status locked"
                    >
                      {phase.status}
                    </span>
                  ) : (
                    // Show clickable status toggle when no invoice
                    <button
                      className="prjctphase-status-badge"
                      style={{ backgroundColor: getStatusColor(phase.status) }}
                      onClick={() => toggleStatus(phase)}
                      title="Toggle status"
                    >
                      {phase.status}
                    </button>
                  )}

                  {phase.status === 'Completed' && !phase.invoice_created && (
                    <button
                      className="prjctphase-btn-invoice prjctphase-btn-invoice-small"
                      onClick={() => handleGenerateInvoice(phase)}
                      disabled={generatingInvoice}
                      title="Generate Invoice"
                    >
                      <FileText size={16} /> Generate
                    </button>
                  )}

                  {!phase.invoice_created && (
                    <button className="prjctphase-btn-icon" onClick={() => startEditing(phase)} title="Edit phase">
                      <Edit2 size={18} />
                    </button>
                  )}

                  <button className="prjctphase-btn-icon" onClick={() => handleDelete(phase.id)} title="Delete phase">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {phase.description && (
                <p className="prjctphase-flow-desc">{phase.description}</p>
              )}

              <div className="prjctphase-flow-meta">
                {(phase.start_date || phase.end_date) && (
                  <div className="prjctphase-meta-item">
                    <Calendar size={16} style={{ marginRight: 6 }} />
                    <span>
                      <strong>Timeline:</strong>{' '}
                      {phase.start_date && new Date(phase.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {phase.start_date && phase.end_date ? ' - ' : ''}
                      {phase.end_date && new Date(phase.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}

                {phase.budget_allocation && (
                  <div className="prjctphase-meta-item">
                    <DollarSign size={16} style={{ marginRight: 6 }} />
                    <span>
                      <strong>Budget:</strong> {phase.budget_allocation}% {project.budget && ` (₹${((project.budget * phase.budget_allocation) / 100).toFixed(2)})`}
                    </span>
                  </div>
                )}
              </div>

              {Array.isArray(phase.deliverables) && phase.deliverables?.length > 0 && (
                <div className="prjctphase-flow-deliverables">
                  <div className="prjctphase-flow-deliverables-head">
                    <Package size={16} />
                    <strong>Deliverables</strong>
                  </div>
                  <ul className="prjctphase-flow-deliverables-list">
                    {phase.deliverables.map((d, idx) => (
                      <li key={d.id || idx}>
                        <span className="prjctphase-deliverable-number">{idx + 1}</span>
                        <div className="prjctphase-deliverable-content">
                          <h4>{d.name}</h4>
                          {d.description && <p>{d.description}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit form */}
      {isModalOpen && (
        <div className="prjctphase-modal-overlay" onClick={closeModal}>
          <div className="prjctphase-modal" onClick={(e) => e.stopPropagation()}>
            <div className="prjctphase-form-header">
              <h3>{editingPhaseId ? 'Edit Phase' : 'Create Phase'}</h3>
              <button className="prjctphase-btn-icon" onClick={closeModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <form className="prjctphase-form prjctphase-form--borderless" onSubmit={handleSubmit}>
              <div className="prjctphase-form-grid">
                <div className="prjctphase-form-group">
                  <label>Phase Name</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Design, Development, QA..."
                  />
                </div>

                <div className="prjctphase-form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    name="phase_order"
                    value={formData.phase_order}
                    onChange={handleInputChange}
                    min={1}
                  />
                </div>

                <div className="prjctphase-form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="Not Active">Not Active</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="prjctphase-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date || ''}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="prjctphase-form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date || ''}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="prjctphase-form-group">
                  <label>Budget Allocation (%)</label>
                  <input
                    type="number"
                    name="budget_allocation"
                    value={formData.budget_allocation}
                    onChange={handleInputChange}
                    min={0}
                    max={100}
                  />
                </div>

                <div className="prjctphase-form-group prjctphase-full">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe what this phase covers..."
                  />
                </div>
              </div>

              <div className="prjctphase-deliverables">
                <h4>Deliverables</h4>
                <div className="prjctphase-deliverable-input">
                  <input
                    name="name"
                    value={deliverableInput.name}
                    onChange={handleDeliverableChange}
                    placeholder="Deliverable name"
                  />
                  <input
                    name="description"
                    value={deliverableInput.description}
                    onChange={handleDeliverableChange}
                    placeholder="Short description"
                  />
                  <button type="button" className="prjctphase-btn-add" onClick={addDeliverable}>
                    <Plus size={16} />
                  </button>
                </div>

                <div className="prjctphase-deliverable-list">
                  {formData.deliverables.map((d) => (
                    <div key={d.id} className="prjctphase-deliverable-item">
                      <div>
                        <strong>{d.name}</strong>
                        {d.description && <p>{d.description}</p>}
                      </div>
                      <button type="button" className="prjctphase-btn-icon" onClick={() => removeDeliverable(d.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="prjctphase-form-actions">
                <button type="button" className="prjctphase-btn-secondary" onClick={closeModal}>
                  <X size={16} /> Cancel
                </button>
                <button type="submit" className="prjctphase-btn-primary">
                  <Save size={16} /> {editingPhaseId ? 'Save Changes' : 'Create Phase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPhases;